import { useEffect, useMemo } from 'react'
import qs from 'qs'

import { useQuery } from '@tanstack/react-query'

import { useApi } from '@tabeeb/modules/shared/utils/API/hooks'

import { CertificatesQueryKeys } from '../queries'
import type { CertificateDetails } from '../types'

interface UseCertificateParams {
  id?: number
  subdomain?: string | null
  enabled?: boolean
  onError?: () => void
}

const useCertificate = ({ id, enabled = true, subdomain = null, onError }: UseCertificateParams) => {
  const { certificateServiceApi: api, mainApi } = useApi()

  const queryKey = [CertificatesQueryKeys.Certificate, id, subdomain]
  const query = useQuery({
    queryKey,
    queryFn: async () => {
      const certificate = await api.certificates.getByIdAsync(id!, {
        headers: { 'X-Tenant-Subdomain': subdomain ?? false },
      })

      if (!certificate.OwnerIdentityGuid) {
        return certificate as CertificateDetails
      }

      const ownersResponse = await mainApi.users.getUsersByIdentityGuidsAsync(
        {
          IdentityGuids: [certificate.OwnerIdentityGuid],
        },
        {
          paramsSerializer: (params) => qs.stringify(params, { allowDots: true }),
        }
      )

      const owner = ownersResponse?.[0]

      return {
        ...certificate,
        Owner: owner,
      } as CertificateDetails
    },
    enabled: enabled && Boolean(id),
  })

  useEffect(() => {
    if (query.isError) {
      onError?.()
    }
  }, [query.isError, onError])

  return useMemo(
    () => ({
      loading: query.isLoading || query.isFetching,
      loaded: query.isSuccess,
      certificate: query.data,
    }),
    [query.isLoading, query.isFetching, query.isSuccess, query.data]
  )
}

export default useCertificate
